// "My Pipeline Deals By Stage" funnel — mirrors Zoho's dashboard widget.
// Uses Recharts' FunnelChart primitive. Each stage is a trapezoidal band; the
// width is proportional to either deal count or pipeline value (toggleable).
import { useState } from "react";
import { FunnelChart, Funnel, LabelList, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export interface FunnelStage {
  stage: string;
  label: string;
  count: number;
  valueUsd: number;
}

interface Props {
  data: FunnelStage[];
}

const COLORS = ["#2dd4bf", "#60a5fa", "#f87171", "#34d399"];

export function PipelineFunnel({ data }: Props) {
  const [mode, setMode] = useState<"count" | "value">("count");

  const hasAny = data.some((s) => s.count > 0);

  // Recharts needs a numeric "value" field. We clone the data and map what's selected.
  const chartData = data.map((s, i) => ({
    name: `${s.label} : ${mode === "count" ? s.count : `$${Math.round(s.valueUsd).toLocaleString()}`}`,
    // A minimum width floor so zero-count stages still render a sliver.
    value: (mode === "count" ? s.count : s.valueUsd) || 0.0001,
    fill: COLORS[i % COLORS.length],
  }));

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between pb-2">
        <CardTitle className="text-base">My Pipeline Deals By Stage</CardTitle>
        <div className="flex overflow-hidden rounded-md border text-xs">
          <button
            onClick={() => setMode("count")}
            className={`px-2 py-1 ${mode === "count" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
          >
            Count
          </button>
          <button
            onClick={() => setMode("value")}
            className={`border-l px-2 py-1 ${mode === "value" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
          >
            Value
          </button>
        </div>
      </CardHeader>
      <CardContent>
        {!hasAny ? (
          <p className="py-12 text-center text-sm text-muted-foreground">
            No open deals in the Consulting or Products pipeline yet.
          </p>
        ) : (
          <div style={{ width: "100%", height: 320 }}>
            <ResponsiveContainer>
              <FunnelChart>
                <Tooltip
                  formatter={(val: any, _name, item) => {
                    if (mode === "value") return [`$${Math.round(Number(val)).toLocaleString()}`, item?.payload?.name];
                    return [Number(val).toFixed(0), item?.payload?.name];
                  }}
                />
                <Funnel dataKey="value" data={chartData} isAnimationActive>
                  {chartData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  <LabelList
                    position="right"
                    dataKey="name"
                    fill="#1f2937"
                    style={{ fontSize: 12 }}
                  />
                </Funnel>
              </FunnelChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
